import React from "react";
import { RevealText } from "./RevealText";
import { Cog, Cpu, Workflow, Zap, Database, Bot } from "lucide-react";

export const EngineeringMindset = () => {
  return (
    <section
      id="engineering"
      data-testid="engineering-section"
      className="relative py-24 sm:py-32 overflow-hidden border-t border-white/5"
    >
      {/* Subtle ambient purple lighting */}
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-[#B026FF]/15 blur-[140px] pointer-events-none" />
      <div className="absolute -right-40 bottom-0 w-[420px] h-[420px] rounded-full bg-[#5B0FA8]/20 blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* LEFT — text */}
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#B026FF] mb-5 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B026FF] shadow-[0_0_8px_rgba(176,38,255,0.9)]" />
              The Engineering Mindset
            </p>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
              <RevealText>We don&apos;t just build websites.</RevealText>
              <br />
              <RevealText delay={220} className="neon-text">
                We engineer digital efficiency.
              </RevealText>
            </h2>

            <p className="mt-7 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
              Our approach fuses aesthetic, conversion-focused front-end
              experiences with the silent power of backend AI and workflow
              automation. Every pixel ships with a purpose, and every workflow
              has a measurable ROI. The result: digital systems that don&apos;t
              just look good — they make your business compounding-faster every
              week.
            </p>

            {/* Pillars */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                { Icon: Cpu, title: "Front-end craftsmanship", desc: "Pixel-perfect, fast, accessible." },
                { Icon: Workflow, title: "Backend automation", desc: "n8n, Python, scheduled agents." },
                { Icon: Bot, title: "AI integration", desc: "LLM-powered features, end-to-end." },
                { Icon: Zap, title: "Measurable ROI", desc: "Every system reports its impact." },
              ].map(({ Icon, title, desc }, i) => (
                <div
                  key={title}
                  data-testid={`engineering-pillar-${i}`}
                  className="flex gap-3 p-4 rounded-xl border border-white/8 bg-white/[0.02] hover:border-[#B026FF]/40 hover:bg-[#B026FF]/[0.04] transition-colors"
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-[#B026FF]/10 border border-[#B026FF]/30 text-[#B026FF] flex items-center justify-center">
                    <Icon size={16} strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium">{title}</div>
                    <div className="text-xs text-white/55 mt-0.5">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — animated visual */}
          <div data-testid="engineering-visual" className="relative">
            <div className="relative aspect-square w-full max-w-[560px] mx-auto">
              {/* Glowing core */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-40 h-40 rounded-full bg-gradient-to-br from-[#B026FF] to-[#5B0FA8] flex items-center justify-center shadow-[0_0_80px_rgba(176,38,255,0.6),inset_0_0_40px_rgba(255,255,255,0.15)] eng-pulse-core">
                  <Cpu size={48} className="text-white/95" strokeWidth={1.4} />
                </div>
              </div>

              {/* Concentric orbits */}
              <Orbit size={64} duration="22s" reverse={false} />
              <Orbit size={80} duration="32s" reverse={true} />
              <Orbit size={96} duration="44s" reverse={false} />

              {/* Orbiting nodes */}
              <OrbitNode radius={200} duration="22s" angleStart={0} reverse={false}>
                <NodeBadge Icon={Database} label="DATA" />
              </OrbitNode>
              <OrbitNode radius={200} duration="22s" angleStart={120} reverse={false}>
                <NodeBadge Icon={Workflow} label="N8N" />
              </OrbitNode>
              <OrbitNode radius={200} duration="22s" angleStart={240} reverse={false}>
                <NodeBadge Icon={Bot} label="AI" />
              </OrbitNode>
              <OrbitNode radius={250} duration="32s" angleStart={45} reverse={true}>
                <NodeBadge Icon={Zap} label="API" small />
              </OrbitNode>
              <OrbitNode radius={250} duration="32s" angleStart={225} reverse={true}>
                <NodeBadge Icon={Cog} label="OPS" small />
              </OrbitNode>

              {/* Floating data dots flowing toward the core */}
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(6)].map((_, i) => (
                  <span
                    key={i}
                    className="eng-data-dot"
                    style={{
                      animationDelay: `${i * 0.7}s`,
                      transform: `rotate(${i * 60}deg)`,
                    }}
                  />
                ))}
              </div>

              {/* Corner ticks for that engineering blueprint feel */}
              <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[#B026FF]/40" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[#B026FF]/40" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[#B026FF]/40" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[#B026FF]/40" />

              {/* HUD label */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 font-mono-ui text-[10px] uppercase tracking-widest text-white/45 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                ENGINE_RUNNING · 0.998 throughput
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Orbit = ({ size, duration, reverse }) => (
  <span
    aria-hidden="true"
    className="absolute top-1/2 left-1/2 rounded-full border border-[#B026FF]/15 eng-orbit"
    style={{
      width: `${size}%`,
      height: `${size}%`,
      transform: "translate(-50%, -50%)",
      animationDuration: duration,
      animationDirection: reverse ? "reverse" : "normal",
    }}
  >
    {/* Highlight tick along orbit */}
    <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#B026FF] shadow-[0_0_12px_rgba(176,38,255,0.9)]" />
  </span>
);

const OrbitNode = ({ radius, duration, angleStart, reverse, children }) => (
  <span
    aria-hidden="true"
    className="absolute top-1/2 left-1/2 eng-orbit-node"
    style={{
      width: `${radius}px`,
      height: `${radius}px`,
      transform: `translate(-50%, -50%) rotate(${angleStart}deg)`,
      animationDuration: duration,
      animationDirection: reverse ? "reverse" : "normal",
    }}
  >
    <span
      className="absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-1/2 eng-orbit-counter"
      style={{
        animationDuration: duration,
        animationDirection: reverse ? "normal" : "reverse",
      }}
    >
      {children}
    </span>
  </span>
);

const NodeBadge = ({ Icon, label, small = false }) => (
  <span
    className={`flex flex-col items-center gap-1 ${
      small ? "scale-90" : ""
    }`}
  >
    <span className="relative w-12 h-12 rounded-xl bg-[#0A0510]/90 border border-[#B026FF]/40 backdrop-blur-md flex items-center justify-center shadow-[0_0_24px_rgba(176,38,255,0.35)]">
      <Icon size={18} className="text-[#B026FF]" strokeWidth={1.8} />
    </span>
    <span className="font-mono-ui text-[9px] tracking-widest text-white/65">
      {label}
    </span>
  </span>
);
